import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './modules/auth/pages/LoginPage';
import Home from './modules/home/pages/HomePage';
import { ForgotPasswordPage } from './modules/auth/pages/ForgotPasswordPage';
import ProfilePage from './modules/user/pages/ProfilePage';
import RequestsPage from './modules/requests/pages/RequestsPage';
import PendenciasPage from './modules/pendencias/pages/PendenciasPage';
import MainLayout from './layouts/MainLayout';
import ProtocoloOnlinePage from './modules/protocolo/pages/ProtocoloOnlinePage';
import ConsultaProtocolosPage from './modules/protocolo/pages/ConsultaProtocolosPage';
import CertidoesOnlinePage from './modules/certidoesOnline/pages/CertidoesOnlinePage';
import ValidarCertidaoPage from './modules/certidoesOnline/pages/ValidarCertidaoPage';
import { SelecaoPedidoPage, DynamicPedidoPage, ComissoesPage, AtualizacaoCadastralPage, CertidoesPage } from './modules/requerimentos';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Carregando...</div>;
  }

  // Guarda de onde a pessoa veio (link direto pra um pedido, por exemplo) pra
  // LoginForm.js poder mandar de volta pra lá depois do login — sem isso,
  // qualquer acesso sem sessão sempre caía em /home depois de autenticar. Não
  // muda a proteção em si: sem isAuthenticated, cai em /login de qualquer jeito.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const ProtectedLayout = ({ children }) => (
  <ProtectedRoute>
    <MainLayout>{children}</MainLayout>
  </ProtectedRoute>
);

const ProtectedRoutes = () => {
  return (
    <>
      {/* Rotas principais */}
      <Route path="/home" element={<ProtectedLayout><Home /></ProtectedLayout>} />
      <Route path="/perfil" element={<ProtectedLayout><ProfilePage /></ProtectedLayout>} />
      <Route path="/solicitacoes" element={<ProtectedLayout><RequestsPage /></ProtectedLayout>} />
      <Route path="/pendencias" element={<ProtectedLayout><PendenciasPage /></ProtectedLayout>} />
      <Route path="/protocolo-online" element={<ProtectedLayout><ProtocoloOnlinePage /></ProtectedLayout>} />
      <Route path="/consulta-protocolos" element={<ProtectedLayout><ConsultaProtocolosPage /></ProtectedLayout>} />
      <Route path="/certidoes-online" element={<ProtectedLayout><CertidoesOnlinePage /></ProtectedLayout>} />
      
      {/* Comissões da OAB/MS: departamento próprio, lista simples (sem categorias) */}
      <Route path="/requerimentos/comissoes/pedidos" element={<ProtectedLayout><ComissoesPage /></ProtectedLayout>} />

      {/* Atualização Cadastral: departamento próprio, lista simples (sem categorias) */}
      <Route path="/requerimentos/atualizacao-cadastral/pedidos" element={<ProtectedLayout><AtualizacaoCadastralPage /></ProtectedLayout>} />

      {/* Certidões: departamento próprio, lista simples (sem categorias) */}
      <Route path="/requerimentos/certidoes/pedidos" element={<ProtectedLayout><CertidoesPage /></ProtectedLayout>} />

      {/* Lista de pedidos do departamento */}
      <Route path="/requerimentos/:departamentoId/pedidos" element={<ProtectedLayout><SelecaoPedidoPage /></ProtectedLayout>} />

      {/* Formulário do pedido */}
      <Route path="/requerimentos/:departamentoId/:pedidoId" element={<ProtectedLayout><DynamicPedidoPage /></ProtectedLayout>} />
      
      {/* Redirecionamento para manter compatibilidade com links antigos */}
      <Route path="/selecao-inscricao" element={<Navigate to="/requerimentos/selecao-inscricao/pedidos" replace />} />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Rotas públicas */}
          <Route path="/login" element={<Login />} />
          <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />

          {/* Validação da Certidão Nada Consta: aberta pelo QR Code, sem login */}
          <Route path="/validar-certidao/:codigo" element={<ValidarCertidaoPage />} />
          
          {/* Rotas protegidas */}
          {ProtectedRoutes()}
          
          {/* Redirecionamentos */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;